/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { LuisId } from 'src/luis/types/Resource';
import { ConversationFeaturePickerContainer } from 'src/luis/verticals/conversationFeaturePicker/ConversationFeaturePickerContainer';
import {
    SelectOrCreateEntityRole,
    SelectOrCreateEntityRoleProps
} from 'src/luis/verticals/labelingSidePane/components/SelectOrCreateEntityRole';

const scopedTheme = getScopedTheme('AdvancedOptionsPane');

const messages = defineMessages({
    features: {
        id: 'luis.intentLabelingSidePane.advancedOptions.mlEntity.features',
        defaultMessage: 'Machine learning features'
    },
    featureDescription: {
        id: 'luis.intentLabelingSidePane.advancedOptions.mlEntity.featureDescription',
        defaultMessage:
            'Machine learning features linked to specifically to this part of the entity. An entity or subentity that does not have subentities can use one feature as a requirement.'
    }
});

const featureTitleStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { textTransform: 'uppercase', fontSize: 10, color: scopedTheme.titleTextColor }
});

type Props = SelectOrCreateEntityRoleProps & {
    /**
     * Simple entity to show advanced options for.
     */
    entity: SimpleEntity;
    /**
     * Id of the root entity in the simple entity tree.
     */
    rootEntityId: LuisId;
};

/**
 * Renders advanced options for a ml entity.
 */
export const MLEntityAdvancedOptions = React.memo((props: Props) => {
    const { entity, rootEntityId, ...roleProps } = props;
    const { localize } = useLocalization();

    const showRoles = entity.id === rootEntityId && entity.roles.length;

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            {showRoles && <SelectOrCreateEntityRole entity={entity} {...roleProps} />}
            <Stack horizontal verticalAlign="center">
                <LuisText styles={featureTitleStyles} fontWeight={600}>
                    {localize(messages.features)}
                </LuisText>
                <CalloutButton content={localize(messages.featureDescription)} />
            </Stack>
            <ConversationFeaturePickerContainer
                telemetryContext="MLEntityAdvancedOptions"
                model={entity}
                simpleEntityRootId={rootEntityId}
            />
        </Stack>
    );
});
